/**
 * MyPokemonImportBar — the global x/y progress strip for a running Poké Genie
 * import. Mounted once in App so it follows the trainer to ANY page: the
 * import keeps running while they browse (2026-08-20 report: "I imported mine
 * and didn't know if it was working"). Shows the live row count while
 * importing, then a short summary (including what it ticked in the Pokédex),
 * then hides itself.
 */
import { X } from "lucide-react";

import {
  dismissMyPokemonImport,
  useMyPokemonImport,
} from "@/lib/myPokemonImportRunner";

export function MyPokemonImportBar() {
  const state = useMyPokemonImport();
  if (state.phase === "idle") return null;

  const pct =
    state.total > 0 ? Math.min(100, Math.round((state.done / state.total) * 100)) : 0;

  let line: string;
  if (state.phase === "importing") {
    line = `Importing ${state.done.toLocaleString()} / ${state.total.toLocaleString()} Pokémon`;
  } else if (state.phase === "error") {
    line = state.error ?? "Import failed.";
  } else {
    const s = state.summary;
    const bits: string[] = [];
    if (s) {
      bits.push(`${s.created.toLocaleString()} imported`);
      if (s.merged > 0) bits.push(`${s.merged.toLocaleString()} merged`);
      if (s.skipped > 0) bits.push(`${s.skipped.toLocaleString()} skipped`);
    }
    if (state.rejected > 0) bits.push(`${state.rejected.toLocaleString()} unreadable`);
    if (state.costumesUnrecognised > 0) {
      const n = state.costumesUnrecognised;
      bits.push(`${n.toLocaleString()} ${n === 1 ? "costume" : "costumes"} not recognised (check the glossary)`);
    }
    if (state.dex && state.dex.total > 0) {
      bits.push(`Pokédex updated (${state.dex.total.toLocaleString()} entries)`);
    }
    line = bits.length > 0 ? bits.join(", ") : "Import finished.";
  }

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl overflow-hidden"
      style={{
        bottom: "calc(var(--nx-safe-bottom) + 84px)",
        background: "var(--surface-dark-9)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
      }}
      data-testid="my-pokemon-import-bar"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <p
          // An error must be read whole (Discord, 2026-09-15: "It needs the…"
          // was all an Android tester saw); progress lines stay one line.
          className={`text-[12px] font-bold flex-1 min-w-0 ${state.phase === "error" ? "whitespace-normal break-words" : "truncate"}`}
          style={{
            color: state.phase === "error" ? "hsl(0 70% 60%)" : "var(--text-ink-bright)",
          }}
          data-testid="my-pokemon-import-bar-text"
        >
          {line}
        </p>
        {state.phase !== "importing" && (
          <button
            type="button"
            onClick={dismissMyPokemonImport}
            aria-label="Dismiss import status"
            data-testid="my-pokemon-import-bar-dismiss"
            className="grid place-items-center rounded-full shrink-0"
            style={{ width: 28, height: 28, color: "var(--text-tertiary)" }}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {state.phase === "importing" && (
        <div style={{ height: 3, background: "var(--accent-fill-glow-subtle)" }}>
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "var(--accent-fill)",
              transition: "width 200ms linear",
            }}
            data-testid="my-pokemon-import-bar-fill"
          />
        </div>
      )}
    </div>
  );
}
